import { Nomenklature } from "../../../classes/Nomenklature";
import {
    BeamInterface,
    BeamsNormalize,
    NomenklatureInterface,
    SectionInterface,
    SectionNomenklature,
    StoreWallInterface,
} from "../../types/store";
import { useStoreSystem } from "../../useStoreSystem";
import { useShelvingStore } from "../../useShelvingStore";
import { ItemI, NewNomenkl } from "../../../shared";
import { isNewSite } from "../../../utils";

export const getUpdateNomenkature = (rack: StoreWallInterface) => {
    const isNew = isNewSite();
    const colorsItems = useShelvingStore.getState().colorsItems;
    const parts: ItemI[] | undefined = useStoreSystem.getState().parts;

    const newNomenklature: NomenklatureInterface = new Nomenklature();
    if (!rack.firstRackHeight) {
        return {
            current: 0,
            old: 0,
            nomenklature: { ...newNomenklature },
            sectionsNomenklature: "",
            remainderRight: 0,
            remainderLeft: 0,
        };
    }

    const sections = rack.sections;
    const beams = rack.beams;

    const beamsNormalize: BeamsNormalize = beams.reduce((prev, curr) => {
        return {
            ...prev,
            [curr.sectionId + curr.width]: { ...curr, count: 1 + (prev?.[curr.sectionId + curr.width] ? 1 : 0) },
        };
    }, {} as BeamsNormalize);

    const racks: SectionInterface[] = [];
    beams.forEach((beam: BeamInterface) => {
        newNomenklature.horizontal[beam.width]++;
    });
    sections.forEach((section, index, arr) => {
        if (section.isIndent) return;
        if (index === 0 && !section?.isIndent) {
            racks.push({ ...section, items: [], height: section.leftRackHeight });
            racks.push({ ...section, items: [] });
            return;
        }
        if (arr[index - 1]?.isIndent) {
            racks.push({ ...section, items: [], height: section.leftRackHeight, leftRackHeight: 0 });
            racks.push({ ...section, items: [] });
            return;
        }
        racks.push({ ...section, items: [] });
    });

    const offerIndex = new Map<string, ItemI>();
    if (Array.isArray(parts)) {
        for (const p of parts) {
            if (p.BITRIX_OFFER_ID) offerIndex.set(String(p.BITRIX_OFFER_ID), p);
            if (Array.isArray(p.BITRIX_OFFERS_IDS)) {
                for (const id of p.BITRIX_OFFERS_IDS) {
                    if (id) offerIndex.set(String(id), p);
                }
            }
        }
    }

    const colorGuides = colorsItems?.brackets;

    const resolveIdByColor = (originalId?: string) => {
        if (!isNew || !originalId || !colorGuides) return { id: originalId as string | undefined };
        const src = offerIndex.get(String(originalId));
        const nm: NewNomenkl | undefined =
            // @ts-ignore
            src?.newNomenkl?.find((n) => n.color === colorGuides) || src?.newNomenkl?.[0];
        if (nm?.bitrixId) return { id: nm.bitrixId };
        return { id: originalId };
    };

    const horizontalGuidesId: Record<string, string> = {};
    const verticalGuidesId: Record<string, string> = {};
    parts
        ?.filter((part) => part.NAME.toLowerCase().includes("горизонталь"))
        .forEach((part) => {
            horizontalGuidesId[+part.PROPERTIES.LENGTH - 32] = part.BITRIX_OFFER_ID;
        });
    parts
        ?.filter((part) => part.NAME.toLowerCase().includes("вертикаль"))
        .forEach((part) => {
            verticalGuidesId[part.PROPERTIES.LENGTH] = part.BITRIX_OFFER_ID;
        });
    const connectorBitrixIdBase =
        parts?.find((part) => part.NAME.toLowerCase().includes("соединитель"))?.BITRIX_OFFER_ID || "0";

    const horizontalGuidesIdSel: Record<string, string | undefined> = {
        "480": resolveIdByColor(horizontalGuidesId["480"]).id,
        "600": resolveIdByColor(horizontalGuidesId["600"]).id,
        "800": resolveIdByColor(horizontalGuidesId["800"]).id,
        "1200": resolveIdByColor(horizontalGuidesId["1200"]).id,
    };
    const verticalGuidesIdSel: Record<string, string | undefined> = {
        "480": resolveIdByColor(verticalGuidesId["480"]).id,
        "600": resolveIdByColor(verticalGuidesId["600"]).id,
        "800": resolveIdByColor(verticalGuidesId["800"]).id,
        "1200": resolveIdByColor(verticalGuidesId["1200"]).id,
    };
    const connectorBitrixIdSel = resolveIdByColor(connectorBitrixIdBase).id;

    const sectionNomenkl: SectionNomenklature[] = [];
    racks.forEach((r) => {
        const height = r.height;

        const count480H = Math.ceil(height / 480);
        const count600H = Math.ceil(height / 600);
        const count800H = Math.ceil(height / 800);
        const count1200H = Math.ceil(height / 1200);

        let minRemainder = { 480: 0, 600: 0, 800: 0, 1200: 0, value: Infinity as number };

        for (let i = 0; i <= count480H; i++) {
            for (let j = 0; j <= count600H; j++) {
                for (let k = 0; k <= count800H; k++) {
                    for (let l = 0; l <= count1200H; l++) {
                        const totalLengthRack = i * 480 + j * 600 + k * 800 + l * 1200;
                        if (totalLengthRack < height) continue;
                        if (totalLengthRack / height > 2) continue;
                        const tempMinRemainder = totalLengthRack - height;
                        if (minRemainder.value > tempMinRemainder) {
                            minRemainder = { 480: i, 600: j, 800: k, 1200: l, value: tempMinRemainder };
                        }
                    }
                }
            }
        }

        newNomenklature.vertical["1200"] = newNomenklature.vertical["1200"] + minRemainder["1200"];
        newNomenklature.vertical["800"] = newNomenklature.vertical["800"] + minRemainder["800"];
        newNomenklature.vertical["600"] = newNomenklature.vertical["600"] + minRemainder["600"];
        newNomenklature.vertical["480"] = newNomenklature.vertical["480"] + minRemainder["480"];
        newNomenklature.connector =
            newNomenklature.connector +
            minRemainder["1200"] +
            minRemainder["800"] +
            minRemainder["600"] +
            minRemainder["480"] -
            1;

        const currNomenklIndex = sectionNomenkl.findIndex((nomenkl) => nomenkl.sectionId === r.id);
        if (currNomenklIndex !== -1) {
            sectionNomenkl[currNomenklIndex] = {
                ...sectionNomenkl[currNomenklIndex],
                vertical: {
                    "480": sectionNomenkl[currNomenklIndex].vertical["480"] + minRemainder["480"],
                    "600": sectionNomenkl[currNomenklIndex].vertical["600"] + minRemainder["600"],
                    "800": sectionNomenkl[currNomenklIndex].vertical["800"] + minRemainder["800"],
                    "1200": sectionNomenkl[currNomenklIndex].vertical["1200"] + minRemainder["1200"],
                    bitrixId480: verticalGuidesIdSel["480"] ?? sectionNomenkl[currNomenklIndex].vertical.bitrixId480,
                    bitrixId600: verticalGuidesIdSel["600"] ?? sectionNomenkl[currNomenklIndex].vertical.bitrixId600,
                    bitrixId800: verticalGuidesIdSel["800"] ?? sectionNomenkl[currNomenklIndex].vertical.bitrixId800,
                    bitrixId1200: verticalGuidesIdSel["1200"] ?? sectionNomenkl[currNomenklIndex].vertical.bitrixId1200,
                },
                connector:
                    sectionNomenkl[currNomenklIndex].connector +
                    minRemainder["1200"] +
                    minRemainder["800"] +
                    minRemainder["600"] +
                    minRemainder["480"] -
                    1,
                remainderLeft: sectionNomenkl[currNomenklIndex].remainderRight,
                remainderRight: minRemainder.value,
                bitrixIdConnector: connectorBitrixIdSel ?? sectionNomenkl[currNomenklIndex].bitrixIdConnector,
            };
        } else {
            const newSectionNomenklature: SectionNomenklature = {
                sectionId: r.id,
                horizontal: {
                    "480": beamsNormalize[r.id + "480"]?.width === 480 ? beamsNormalize[r.id + "480"]?.count || 1 : 0,
                    "600": beamsNormalize[r.id + "600"]?.width === 600 ? beamsNormalize[r.id + "600"]?.count || 1 : 0,
                    "800": beamsNormalize[r.id + "800"]?.width === 800 ? beamsNormalize[r.id + "800"]?.count || 1 : 0,
                    "1200":
                        beamsNormalize[r.id + "1200"]?.width === 1200 ? beamsNormalize[r.id + "1200"]?.count || 1 : 0,
                    bitrixId480: horizontalGuidesIdSel["480"] ?? horizontalGuidesId["480"],
                    bitrixId600: horizontalGuidesIdSel["600"] ?? horizontalGuidesId["600"],
                    bitrixId800: horizontalGuidesIdSel["800"] ?? horizontalGuidesId["800"],
                    bitrixId1200: horizontalGuidesIdSel["1200"] ?? horizontalGuidesId["1200"],
                },
                vertical: {
                    "480": minRemainder["480"],
                    "600": minRemainder["600"],
                    "800": minRemainder["800"],
                    "1200": minRemainder["1200"],
                    bitrixId480: verticalGuidesIdSel["480"] ?? verticalGuidesId["480"],
                    bitrixId600: verticalGuidesIdSel["600"] ?? verticalGuidesId["600"],
                    bitrixId800: verticalGuidesIdSel["800"] ?? verticalGuidesId["800"],
                    bitrixId1200: verticalGuidesIdSel["1200"] ?? verticalGuidesId["1200"],
                },
                connector: minRemainder["1200"] + minRemainder["800"] + minRemainder["600"] + minRemainder["480"] - 1,
                bitrixIdConnector: connectorBitrixIdSel ?? connectorBitrixIdBase,
                remainderRight: minRemainder.value,
            };
            sectionNomenkl.push(newSectionNomenklature);
        }
    });

    const racksPrice = { price: 0, oldPrice: 0 };

    const findPricesById = (elementId?: string) => {
        if (!elementId) return { price: 0, oldPrice: 0 };

        const p = isNew
            ? parts?.find((part) => part.BITRIX_OFFERS_IDS?.includes(elementId))
            : parts?.find((part) => part.BITRIX_OFFER_ID === elementId);

        return {
            price: p?.PRICE ?? 0,
            oldPrice: p?.OLD_PRICE ?? 0,
        };
    };
    const sectionNomenklatureWithPrices: SectionNomenklature[] = sectionNomenkl.map((section) => {
        const horizontal480 = findPricesById(section.horizontal.bitrixId480);
        const horizontal600 = findPricesById(section.horizontal.bitrixId600);
        const horizontal800 = findPricesById(section.horizontal.bitrixId800);
        const horizontal1200 = findPricesById(section.horizontal.bitrixId1200);

        racksPrice.price += section.horizontal["480"] * horizontal480.price;
        racksPrice.price += section.horizontal["600"] * horizontal600.price;
        racksPrice.price += section.horizontal["800"] * horizontal800.price;
        racksPrice.price += section.horizontal["1200"] * horizontal1200.price;

        racksPrice.oldPrice += section.horizontal["480"] * horizontal480.oldPrice;
        racksPrice.oldPrice += section.horizontal["600"] * horizontal600.oldPrice;
        racksPrice.oldPrice += section.horizontal["800"] * horizontal800.oldPrice;
        racksPrice.oldPrice += section.horizontal["1200"] * horizontal1200.oldPrice;

        const vertical480 = findPricesById(section.vertical.bitrixId480);
        const vertical600 = findPricesById(section.vertical.bitrixId600);
        const vertical800 = findPricesById(section.vertical.bitrixId800);
        const vertical1200 = findPricesById(section.vertical.bitrixId1200);

        racksPrice.price += section.vertical["480"] * vertical480.price;
        racksPrice.price += section.vertical["600"] * vertical600.price;
        racksPrice.price += section.vertical["800"] * vertical800.price;
        racksPrice.price += section.vertical["1200"] * vertical1200.price;

        racksPrice.oldPrice += section.vertical["480"] * vertical480.oldPrice;
        racksPrice.oldPrice += section.vertical["600"] * vertical600.oldPrice;
        racksPrice.oldPrice += section.vertical["800"] * vertical800.oldPrice;
        racksPrice.oldPrice += section.vertical["1200"] * vertical1200.oldPrice;

        const connectorPrice = findPricesById(section.bitrixIdConnector);

        racksPrice.price += section.connector * connectorPrice.price;
        racksPrice.oldPrice += section.connector * connectorPrice.oldPrice;

        return {
            ...section,
            vertical: {
                ...section.vertical,
                price480: vertical480.price,
                price600: vertical600.price,
                price800: vertical800.price,
                price1200: vertical1200.price,
            },
            horizontal: {
                ...section.horizontal,
                price480: horizontal480.price,
                price600: horizontal600.price,
                price800: horizontal800.price,
                price1200: horizontal1200.price,
            },
            connectorPrice: connectorPrice.price,
        };
    });

    return {
        current: racksPrice.price,
        old: racksPrice.oldPrice,
        nomenklature: newNomenklature,
        sectionsNomenklature: sectionNomenklatureWithPrices,
    };
};
